import { create } from 'zustand';

export type ThemeMode = 'dark' | 'light' | 'system';

interface ThemeState {
  theme: ThemeMode;
  resolvedTheme: 'dark' | 'light';
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'cinesync_theme';

function getSystemTheme(): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function suppressTransitionsTemporarily() {
  if (typeof document === 'undefined') return;
  const css = document.createElement('style');
  css.setAttribute('id', 'cinesync-suppress-transitions');
  css.appendChild(
    document.createTextNode(
      `*, *::before, *::after {
        -webkit-transition: none !important;
        -moz-transition: none !important;
        -o-transition: none !important;
        -ms-transition: none !important;
        transition: none !important;
      }`
    )
  );
  document.head.appendChild(css);

  // Force style flush
  if (document.body) {
    void window.getComputedStyle(document.body).opacity;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const el = document.getElementById('cinesync-suppress-transitions');
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  });
}

function applyThemeToDocument(theme: ThemeMode): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'dark';

  suppressTransitionsTemporarily();

  const resolved = theme === 'system' ? getSystemTheme() : theme;
  const root = document.documentElement;

  if (resolved === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.remove('dark');
    root.classList.add('light');
    root.style.colorScheme = 'light';
  }

  return resolved;
}

// Initial load
const initialTheme: ThemeMode = (() => {
  if (typeof window === 'undefined') return 'dark';
  const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
  if (saved && (saved === 'dark' || saved === 'light' || saved === 'system')) {
    return saved;
  }
  return 'dark'; // CineSync defaults to cinematic dark
})();

export const useThemeStore = create<ThemeState>((set, get) => {
  const initialResolved = applyThemeToDocument(initialTheme);

  // Listen for OS system theme changes
  if (typeof window !== 'undefined') {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', () => {
      const currentTheme = get().theme;
      if (currentTheme === 'system') {
        const newResolved = applyThemeToDocument('system');
        set({ resolvedTheme: newResolved });
      }
    });
  }

  return {
    theme: initialTheme,
    resolvedTheme: initialResolved,
    setTheme: (newTheme: ThemeMode) => {
      try {
        localStorage.setItem(STORAGE_KEY, newTheme);
      } catch {}
      const resolved = applyThemeToDocument(newTheme);
      set({ theme: newTheme, resolvedTheme: resolved });
    },
    toggleTheme: () => {
      const current = get().resolvedTheme;
      const nextTheme: ThemeMode = current === 'dark' ? 'light' : 'dark';
      get().setTheme(nextTheme);
    },
  };
});
